import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { api, fmtINR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const EMPTY = { name: "", phone: "", service: "", amount_total: 0, amount_paid: 0, notes: "" };

export default function Vendors() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/admin/vendors").then(({ data }) => setRows(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name) return toast.error("Vendor name required");
    try { await api.post("/admin/vendors", { ...form, amount_total: Number(form.amount_total), amount_paid: Number(form.amount_paid) }); toast.success("Vendor added"); setOpen(false); setForm(EMPTY); load(); }
    catch { toast.error("Failed"); }
  };
  const pay = async (v, amount_paid) => { await api.patch(`/admin/vendors/${v.id}`, { amount_paid: Number(amount_paid) }); toast.success("Updated"); load(); };
  const remove = async (id) => { await api.delete(`/admin/vendors/${id}`); toast.success("Removed"); load(); };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-3xl text-zinc-900">Vendor Payments</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="add-vendor-btn" className="gold-gradient text-white font-semibold"><Plus size={16} className="mr-1" /> Add Vendor</Button></DialogTrigger>
          <DialogContent className="bg-card border-pink-200 text-zinc-900">
            <DialogHeader><DialogTitle className="font-display text-xl">Add Vendor</DialogTitle></DialogHeader>
            <div className="space-y-3">
              {[["name", "Name"], ["phone", "Phone"], ["service", "Service"]].map(([k, l]) => (
                <div key={k}><Label className="text-zinc-700">{l}</Label><Input value={form[k]} data-testid={`vendor-${k}`} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1 bg-white border-pink-200 text-zinc-900" /></div>
              ))}
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-zinc-700">Total (₹)</Label><Input type="number" value={form.amount_total} data-testid="vendor-total" onChange={(e) => setForm({ ...form, amount_total: e.target.value })} className="mt-1 bg-white border-pink-200 text-zinc-900" /></div>
                <div><Label className="text-zinc-700">Paid (₹)</Label><Input type="number" value={form.amount_paid} data-testid="vendor-paid" onChange={(e) => setForm({ ...form, amount_paid: e.target.value })} className="mt-1 bg-white border-pink-200 text-zinc-900" /></div>
              </div>
              <Button onClick={create} data-testid="save-vendor-btn" className="w-full gold-gradient text-white font-semibold rounded-full">Save</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-card border border-pink-200 rounded-xl overflow-hidden overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-pink-50 text-zinc-600 text-left"><tr>{["Vendor", "Service", "Total", "Paid", "Due", ""].map((h) => <th key={h} className="px-4 py-3 font-medium">{h}</th>)}</tr></thead>
          <tbody>
            {rows.map((v) => (
              <tr key={v.id} className="border-t border-pink-100" data-testid={`vendor-row-${v.id}`}>
                <td className="px-4 py-3 text-zinc-900">{v.name}<div className="text-xs text-zinc-500">{v.phone}</div></td>
                <td className="px-4 py-3 text-zinc-700">{v.service}</td>
                <td className="px-4 py-3 text-zinc-700">{fmtINR(v.amount_total)}</td>
                <td className="px-4 py-3">
                  <Input type="number" defaultValue={v.amount_paid} data-testid={`vendor-paid-${v.id}`} onBlur={(e) => e.target.value != v.amount_paid && pay(v, e.target.value)} className="h-8 w-24 bg-white border-pink-200 text-zinc-900" />
                </td>
                <td className="px-4 py-3 text-amber-400">{fmtINR(Math.max(0, v.amount_total - v.amount_paid))}</td>
                <td className="px-4 py-3 text-right"><button onClick={() => remove(v.id)} className="text-zinc-600 hover:text-red-400"><Trash2 size={16} /></button></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-zinc-500">No vendors yet</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
